// src/com/veh_type_group_order/VehTypeGroupOrderEdit.js
import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import {
    DialogContent, Dialog, Button
} from '@mui/material';
import VerticalAlignBottomIcon from '@mui/icons-material/VerticalAlignBottom'; // 맨 뒤로 이동.
import ClearIcon from '@mui/icons-material/Clear'; // cancel moving.
import ReadMoreIcon from '@mui/icons-material/ReadMore'; // insert above this record.
import HeightIcon from '@mui/icons-material/Height'; // 이동할 레코드 선택.
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import ArrowCircleLeftIcon from '@mui/icons-material/ArrowCircleLeft';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import SearchBox from '../../common/SearchBox';
import { AppWord, AppObject, AppStyle } from '../../value/AppConst';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import { VehTypeGroupOrderDictionary } from './VehTypeGroupOrderDict';
import { useAlertSetter } from '../../api/app-hook';
import { vehTypeGroupOrderListRepo } from '../../value/AppModel';
import DialogHeader from '../../common/DialogHeader';
import FlexyTable from '../../common/flexytable/FlexyTable';
import {
    LIST_VEH_TYPE_GROUP_ORDER, // GET_VEH_TYPE_GROUP_ORDER,
    MAP_VEH_TYPE_GROUP_ORDER, REMOVE_VEH_TYPE_GROUP_ORDER, devideTypesByGroupCode
} from './VehTypeGroupOrderGql';

const BigColumn = styled(Box)({
    flexGrow: 1, //backgroundColor: '#f0f0f0',
    display: 'flex', flexDirection: 'column',
});

const ColumnHeader = styled(Box)({
    display: 'flex', alignItems: 'center', padding: 10, boxSizing: 'border-box',
});
const SubTitle = styled(Box)({fontSize:'0.9rem', fontWeight:'bold', flexGrow:1});
const ToolBox = styled(Box)({marginLeft:10});
const InfoText = styled(Box)({
    fontSize:'0.85rem', flexGrow: 1, display:'flex', alignItems:'center', fontWeight:'normal'
});

const TypeAction = {Wait:0, Add:1, Move:2};

export default function VehTypeGroupOrderEdit({
    open,
    vehTypeGroup,
    onRequestClose,
}) {
    const vehTypeGroupOrderListData = useReactiveVar(vehTypeGroupOrderListRepo);
    const [typesInGroup, setTypesInGroup] = useState([]);
    const [typesOutGroup, setTypesOutGroup] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [searchType, setSearchType] = useState('');
    const [editAction, setEditAction] = useState({action:TypeAction.Wait, item:null});
    const openConfirmDialog = useConfirmDialog();
    const addAlert = useAlertSetter();
    const lastReloadRef = useRef(0);

    // ##### Call GraphQL to get List #####
    const [getVehTypeGroupOrderList, responseList] = useLazyQuery(LIST_VEH_TYPE_GROUP_ORDER, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
		onError: (error) => addAlert({title: VehTypeGroupOrderDictionary.errorTitle.Get, message: [error.message]})
    });
    // ##### GraphQL Mutation. 'onError' for prevent from printing error at console (Uncaught exception) ###
    const [mapVehTypeGroupOrder, responseAdd] = useMutation( MAP_VEH_TYPE_GROUP_ORDER, {
		onCompleted: (data, option) => onCompleteMap(data, option), 
		onError: (error) => addAlert({title: VehTypeGroupOrderDictionary.errorTitle.Add, message: [error.message]})
	} );
    const [removeVehTypeGroupOrder, responseRemove] = useMutation( REMOVE_VEH_TYPE_GROUP_ORDER, {
		onCompleted: (data, option) => onCompleteRemove(data, option),
		onError: (error) => addAlert({title: VehTypeGroupOrderDictionary.errorTitle.Remove, message: [error.message]})
	});

    const devideTypes = (list) => {
        const twoGroups = devideTypesByGroupCode(vehTypeGroup.vehTypeGpCd, list);
        setTypesInGroup(twoGroups.inGroup);
        setTypesOutGroup(twoGroups.outGroup);
    };

    const getVehTypeGroupOrderListNow = () => {
        if(vehTypeGroup && vehTypeGroup.vehTypeGpCd) {
            const param = {variables:{vehTypeGpCd:vehTypeGroup.vehTypeGpCd}};
            getVehTypeGroupOrderList(param);
            lastReloadRef.current = new Date().getTime()/1000;
        }
    };

    useEffect(() => {
        devideTypes(vehTypeGroupOrderListData.records);
    }, [vehTypeGroupOrderListData]);

    useEffect(() => {
        setEditAction({action:TypeAction.Wait, item:null});
        if(vehTypeGroup && vehTypeGroup.vehTypeGpCd) {
            const diff = new Date().getTime()/1000 - vehTypeGroupOrderListData.when;
            if(diff > 300)
                getVehTypeGroupOrderListNow();
            else devideTypes(vehTypeGroupOrderListData.records);
        }
    }, [vehTypeGroup]);


    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetList = (data, clientOption) => {
        if(data.vehTypeGroupOrderList) {
            vehTypeGroupOrderListRepo({when:new Date().getTime()/1000, records:data.vehTypeGroupOrderList});
        }
    };

    const onCompleteMap = (data, clientOption) => {
        if(data.vehTypeGroupOrderMap.ok) {
            getVehTypeGroupOrderListNow();
        }
        else addAlert({title:VehTypeGroupOrderDictionary.errorTitle.Add, message:[data.vehTypeGroupOrderMap.message]});
    };

    const onCompleteRemove = (data, clientOption) => {
        if(data.vehTypeGroupOrderRemove.ok) getVehTypeGroupOrderListNow();
        else addAlert({title:VehTypeGroupOrderDictionary.errorTitle.Remove, message:[data.vehTypeGroupOrderRemove.message]});
    };

    // >>>>>>>>> Event Handler <<<<<<<<<<<<<

    const confirmRemove = async (item) => {
        const confirm = await openConfirmDialog({
            title: "차종정보 제외",
            content: [
                `${item.vehTypeName} 차종을 그룹에서 제외하시겠습니까?`,
            ]
        });

        if(confirm)
            removeVehTypeGroupOrder({variables:{vehTypeGroupOrder:{vehTypeId:item.vehTypeId, vehTypeGpCd:item.vehTypeGpCd}}});
    };

    const onChangeSearchText = (text) => {
        setSearchText(text);
    };

    const mapVehType = (movingItem, targetItem) => {
        const param = {
            variables: {
                vehTypeGroupOrder: {
                    vehTypeId: movingItem.vehTypeId,
                    vehTypeGpCd: vehTypeGroup.vehTypeGpCd,
                    vehOrder: targetItem ? targetItem.vehOrder : null
                }
            }
        };
        mapVehTypeGroupOrder(param);
    };

    const onClickPutAtEnd = () => {
        mapVehType({...editAction.item}, null);
        onClickCancelMap();
    };

    const onClickCancelMap = () => {
        setEditAction({action:TypeAction.Wait, item:null});
    };

    const startMoving = (item) => {
        setSearchText('');
        setEditAction({action:TypeAction.Move, item:item});
    };

    const changeOrderNow = (item) => {
        // call api.
        mapVehType({...editAction.item}, item);
        onClickCancelMap();
    };

    const onClickToolOnRow = (toolId, row) => {
        switch(toolId) {
            case AppObject.command.MOVE: startMoving(row); break;
            case AppObject.command.DELETE: confirmRemove(row); break;
            case 'set': changeOrderNow(row); break;
            default: return;
        }
    };

    const onClickToolOnRowOut = (toolId, row) => {
        switch(toolId) {
            case AppObject.command.NEW: startMoving(row); break;
            default: return;
        }
    };

    const tools = editAction.action === TypeAction.Wait ? [
        {id:AppObject.command.MOVE, icon:<HeightIcon/>, title:'순서변경'},
        {id:AppObject.command.DELETE, icon:<DeleteForeverIcon/>, title:'그룹에서 제외', color:AppStyle.mui.warning},
    ] : [
        {id:'set', icon:<ReadMoreIcon/>, title:'이 위에 추가'},
    ];

    const toolsOut = editAction.action === TypeAction.Wait ? [
        {id:AppObject.command.NEW, icon:<ArrowCircleLeftIcon/>, title:'그룹에 추가', },
    ] : [];

    const loadingAny
    = responseList.loading
    || responseAdd.loading
    || responseRemove.loading;

    return(
        <Dialog open={open} fullScreen>
            <DialogHeader
                title={vehTypeGroup.groupName + ' 내 차종 관리'}
                onClose={editAction.action === TypeAction.Wait ? onRequestClose : null}
            />
            <DialogContent style={{display:'flex'}}>
                <BigColumn style={{width:'48%'}}>
                    <ColumnHeader>
                        <SubTitle>그룹 내 차종</SubTitle>
                    </ColumnHeader>
                    <ColumnHeader height={65}>
                        <InfoText>
                            {
                                editAction.action === TypeAction.Wait ?
                                <>
                                    <span>순서를 변경할 차종의</span>
                                    <HeightIcon fontSize={AppWord.SMALL} color={AppStyle.mui.primary} />
                                    <span>아이콘을 클릭하거나 삭제할 수 있습니다.</span>
                                </>
                                :
                                <div style={{display:'inline-flex',alignItems:'center',color:AppStyle.PrimaryRGB, backgroundColor:'#ef0'}}>
                                    <span>선택한 {editAction.item.vehTypeName}차종을 이동할 위치의 </span>
                                    <ReadMoreIcon fontSize={AppWord.SMALL} color={AppStyle.mui.primary} />
                                    <span> 아이콘을 클릭하세요.</span>
                                </div>
                            }
                        </InfoText>
                        <ToolBox>
                            {
                                editAction.action === TypeAction.Wait ?
                                <SearchBox
                                    value={searchText}
                                    onChangeText={onChangeSearchText}
                                    width={120}
                                />
                                :
                                <>
                                    <Button
                                        onClick={onClickPutAtEnd}
                                        variant={AppStyle.mui.contained}
                                        color={AppStyle.mui.secondary}
                                        size={AppWord.SMALL}
                                        startIcon={<VerticalAlignBottomIcon/>}
                                        disabled={loadingAny}
                                    >
                                        맨 뒤에 넣기
                                    </Button>
                                    <Button
                                        onClick={onClickCancelMap}
                                        variant={AppStyle.mui.contained}
                                        color={AppStyle.mui.warning}
                                        size={AppWord.SMALL}
                                        startIcon={<ClearIcon/>}
                                        style={{marginLeft:10}}
                                    >
                                        취소
                                    </Button>
                                </>
                            }
                        </ToolBox>
                    </ColumnHeader>
                    <FlexyTable
                        name={VehTypeGroupOrderDictionary.inTableProps.tableId}
                        tableProps={VehTypeGroupOrderDictionary.inTableProps}
                        records={typesInGroup}
                        search={searchText}
                        tools={tools}
                        onClickTool={onClickToolOnRow}
                    />
                </BigColumn>
                <BigColumn style={{marginLeft:10, width:'42%'}}>
                    <ColumnHeader>
                        <SubTitle>제외 차종</SubTitle>
                    </ColumnHeader>
                    <ColumnHeader height={65}>
                        <InfoText>
                            {
                                editAction.action === TypeAction.Wait ?
                                <>
                                <span
                                    style={{display:'inline-flex', alignItems:'center', }}
                                >그룹에 추가할 차종으로 마우스를 이동하여 
                                <ArrowCircleLeftIcon fontSize={AppWord.SMALL} color={AppStyle.mui.primary} />
                                아이콘을 클릭하세요.</span>
                                </>
                                :
                                <span>좌측 테이블의 지시를 따르세요.</span>
                            }
                        </InfoText>
                        <ToolBox>
                            {
                                editAction.action === TypeAction.Wait ?
                                <SearchBox
                                    value={searchType}
                                    onChangeText={(t)=>setSearchType(t)}
                                    width={120}
                                /> : null
                            }
                        </ToolBox> 
                    </ColumnHeader>
                    <FlexyTable
                        name={VehTypeGroupOrderDictionary.outTableProps.tableId}
                        tableProps={VehTypeGroupOrderDictionary.outTableProps}
                        records={typesOutGroup}
                        search={searchType}
                        tools={toolsOut}
                        onClickTool={onClickToolOnRowOut}
                    />
                </BigColumn>
            </DialogContent>
        </Dialog>
    );
}